import _ from 'lodash';
import { createSelector } from 'reselect';
import { WALL_PANEL_ID } from 'domain/Options/generators/splashbacks';
import parameterMessages from '../parameterMessages';
import * as constants from '../constants';
import parametersSelector from './utils/parameterSelector';
import { materialParameterSelector, wallPanelMaterialParameterSelector, splashbackParameterSelector, plannedShapeParameterSelector, kitchenIslandMaterialParameterSelector, kitchenIslandParameterSelector, thicknessParameterSelector } from './parameters';
import * as planParameters from '../planParameters';
import { NO_KITCHEN_ISLAND_ID } from '../generators/kitchenIslands';
import { NO_WORKTOP_MATERIAL } from '../generators/materials';
import conflictCategories from '../conflictCategories';

export const cutoutsSelector = parametersSelector(planParameters.cutout);
export const freestandingHobsSelector = parametersSelector(planParameters.freestandingHob);
export const singleCutsSelector = parametersSelector(planParameters.singleCut);
export const doubleCutsSelector = parametersSelector(planParameters.doubleCut);
export const tripleCutsSelector = parametersSelector(planParameters.tripleCut);
export const highCabinetJointsSelector = parametersSelector(planParameters.highCabinetJoint);
export const roundedCornersSelector = parametersSelector(planParameters.roundedCorner);
export const wallPanelRoundCutsSmallSelector = parametersSelector(planParameters.wallPanelRoundCutSmall);
export const wallPanelRoundCutsBigSelector = parametersSelector(planParameters.wallPanelRoundCutBig);
export const wallPanelSquareCutsSelector = parametersSelector(planParameters.wallPanelSquareCut);
export const wallPanelSingleCutsSelector = parametersSelector(planParameters.wallPanelSingleCut);
export const wallPanelDoubleCutsSelector = parametersSelector(planParameters.wallPanelDoubleCut);
export const wallPanelTripleCutsSelector = parametersSelector(planParameters.wallPanelTripleCut);
export const invertedCornersSelector = parametersSelector(planParameters.invertedCorner);
export const tapHolesSelector = parametersSelector(planParameters.tapHole);
export const kitchenIslandSingleCutsSelector = parametersSelector(planParameters.kitchenIslandSingleCut);
export const kitchenIslandRoundedCornersSelector = parametersSelector(planParameters.kitchenIslandRoundedCorner);
export const kitchenIslandSquareCutsSelector = parametersSelector(planParameters.kitchenIslandSquareCut);

const makeOperationConflictSelector = (operation, conflictingParameterSelector, extra = null) => {
  return createSelector(
    conflictingParameterSelector,
    (conflictingParameter) => {
      if (conflictingParameter.get('value') != null && !conflictingParameter.getIn(['value', 'operations', operation])) {
        if (extra) {
          return [[extra, ': ', conflictingParameter.getIn(['value', 'displayName']).toJS()]];
        } else {
          return [conflictingParameter.getIn(['value', 'displayName']).toJS()];
        }
      } else {
        return null;
      }
    }
  );
};

const makeHighCabinetConflictSelector = (operation, conflictingParameterSelector, extra = null) => {
  return createSelector(
    conflictingParameterSelector,
    (conflictingParameter) => {
      if (conflictingParameter.getIn(['value', 'id']) === NO_WORKTOP_MATERIAL) {
        return null;
      }
      if (conflictingParameter.get('value') != null && !conflictingParameter.getIn(['value', 'operations', operation])) {
        if(Array.from(conflictingParameter.get('value').get('expressions').get(0).get('thicknesses'))[0].get('priceByArea') == false){
          return null;
        };
        if (extra) {
          return [[extra, ': ', conflictingParameter.getIn(['value', 'displayName']).toJS()]];
        } else {
          return [conflictingParameter.getIn(['value', 'displayName']).toJS()];
        }
      } else {
        return null;
      }
    }
  );
};

const getNestedObject = (nestedObj, pathArr) => {
  return pathArr.reduce((obj, key) =>
      (obj && obj[key] !== 'undefined') ? obj[key] : undefined, nestedObj);
}

const worktopOperationConflictSelector = (operation) => {
  return makeOperationConflictSelector(operation, materialParameterSelector);
};

const worktopHighCabinetConflictSelector = (operation) => {
  return makeHighCabinetConflictSelector(operation, materialParameterSelector,);
};

const kitchenIslandOperationConflictSelector = (operation) => {
  return createSelector(
    makeOperationConflictSelector(operation, kitchenIslandMaterialParameterSelector, parameterMessages.kitchenIslandMaterial),
    kitchenIslandParameterSelector,
    (operationConflicts, kitchenIsland) => {
      if (kitchenIsland.getIn(['value', 'id']) === NO_KITCHEN_ISLAND_ID) {
        return null;
      } else {
        return operationConflicts;
      }
    }
  );
};

const wallPanelOperationConflictSelector = (operation) => {
  return createSelector(
    makeOperationConflictSelector(operation, wallPanelMaterialParameterSelector, parameterMessages.wallPanelMaterial),
    splashbackParameterSelector,
    (operationConflicts, splashback) => {
      if (splashback.getIn(['value', 'id']) !== WALL_PANEL_ID) {
        return null;
      } else {
        return operationConflicts;
      }
    }
  );
};

export const cutoutConflictsSelector = worktopOperationConflictSelector(planParameters.cutout);
export const roundedCornerConflictsSelector = worktopOperationConflictSelector(planParameters.roundedCorner);
export const invertedCornerConflictsSelector = worktopOperationConflictSelector(planParameters.invertedCorner);
export const singleCutConflictsSelector = worktopOperationConflictSelector(planParameters.singleCut);
export const doubleCutConflictsSelector = worktopOperationConflictSelector(planParameters.doubleCut);
export const tripleCutConflictsSelector = worktopOperationConflictSelector(planParameters.tripleCut);
export const highCabinetJointConflictsSelector = worktopHighCabinetConflictSelector(planParameters.highCabinetJoint);
export const tapHoleConflictsSelector = worktopOperationConflictSelector(planParameters.tapHole);
export const wallPanelRoundCutSmallConflictsSelector = wallPanelOperationConflictSelector(planParameters.wallPanelRoundCutSmall);
export const wallPanelRoundCutBigConflictsSelector = wallPanelOperationConflictSelector(planParameters.wallPanelRoundCutBig);
export const wallPanelSquareCutConflictsSelector = wallPanelOperationConflictSelector(planParameters.wallPanelSquareCut);
export const wallPanelSingleCutConflictsSelector = wallPanelOperationConflictSelector(planParameters.wallPanelSingleCut);
export const wallPanelDoubleCutConflictsSelector = wallPanelOperationConflictSelector(planParameters.wallPanelDoubleCut);
export const wallPanelTripleCutConflictsSelector = wallPanelOperationConflictSelector(planParameters.wallPanelTripleCut);

export const kitchenIslandSingleCutsConflictsSelector = kitchenIslandOperationConflictSelector(planParameters.singleCut);
export const kitchenIslandRoundedCornersConflictsSelector = kitchenIslandOperationConflictSelector(planParameters.roundedCorner);
export const kitchenIslandSquareCutsConflictsSelector = kitchenIslandOperationConflictSelector(planParameters.cutout);

export const freestandingHobConflictsSelector = createSelector(
  plannedShapeParameterSelector,
  (plannedShape) => {
    const plannedShapeValue = plannedShape.get('value');
    const plannedShapeId = plannedShapeValue.get('id');

    if (plannedShapeId === 'irregular') {
      return [plannedShapeValue.get('displayName').toJS()];
    }
    return null;
  }
);

const planOperations = {
  highCabinetJoint: {
    actionType: constants.CHANGE_HIGH_CABINET_JOINTS,
    message: parameterMessages.highCabinetJoints,
    parameter: highCabinetJointsSelector,
    conflicts: highCabinetJointConflictsSelector,
  },
};

const worktopOperations = {
  roundedCorner: {
    actionType: constants.CHANGE_ROUNDED_CORNERS,
    message: parameterMessages.roundedCorners,
    parameter: roundedCornersSelector,
    conflicts: roundedCornerConflictsSelector,
  },
  invertedCorner: {
    actionType: constants.CHANGE_INVERTED_CORNERS,
    message: parameterMessages.invertedCorners,
    parameter: invertedCornersSelector,
    conflicts: invertedCornerConflictsSelector,
  },
  cutout: {
    actionType: constants.CHANGE_CUTOUTS,
    message: parameterMessages.cutoutsForHob,
    parameter: cutoutsSelector,
    conflicts: cutoutConflictsSelector,
  },
  singleCut: {
    actionType: constants.CHANGE_SINGLE_CUTS,
    message: parameterMessages.singleCuts,
    parameter: singleCutsSelector,
    conflicts: singleCutConflictsSelector,
  },
  doubleCut: {
    actionType: constants.CHANGE_DOUBLE_CUTS,
    message: parameterMessages.doubleCuts,
    parameter: doubleCutsSelector,
    conflicts: doubleCutConflictsSelector,
  },
  tripleCut: {
    actionType: constants.CHANGE_TRIPLE_CUTS,
    message: parameterMessages.tripleCuts,
    parameter: tripleCutsSelector,
    conflicts: tripleCutConflictsSelector,
  },
  tapHole: {
    actionType: constants.CHANGE_TAP_HOLES,
    message: parameterMessages.tapHoles,
    parameter: tapHolesSelector,
    conflicts: tapHoleConflictsSelector,
  },
};

const wallPanelOperations = {
  wallPanelRoundCutSmall: {
    actionType: constants.CHANGE_WALL_PANEL_ROUND_CUTS_SMALL,
    message: parameterMessages.roundCutsSmall,
    parameter: wallPanelRoundCutsSmallSelector,
    conflicts: wallPanelRoundCutSmallConflictsSelector,
  },
  wallPanelRoundCutBig: {
    actionType: constants.CHANGE_WALL_PANEL_ROUND_CUTS_BIG,
    message: parameterMessages.roundCutsBig,
    parameter: wallPanelRoundCutsBigSelector,
    conflicts: wallPanelRoundCutBigConflictsSelector,
  },
  wallPanelSquareCut: {
    actionType: constants.CHANGE_WALL_PANEL_SQUARE_CUTS,
    message: parameterMessages.squareCuts,
    parameter: wallPanelSquareCutsSelector,
    conflicts: wallPanelSquareCutConflictsSelector,
  },
  wallPanelSingleCut: {
    actionType: constants.CHANGE_WALL_PANEL_SINGLE_CUTS,
    message: parameterMessages.singleCuts,
    parameter: wallPanelSingleCutsSelector,
    conflicts: wallPanelSingleCutConflictsSelector,
  },
  wallPanelDoubleCut: {
    actionType: constants.CHANGE_WALL_PANEL_DOUBLE_CUTS,
    message: parameterMessages.doubleCuts,
    parameter: wallPanelDoubleCutsSelector,
    conflicts: wallPanelDoubleCutConflictsSelector,
  },
  wallPanelTripleCut: {
    actionType: constants.CHANGE_WALL_PANEL_TRIPLE_CUTS,
    message: parameterMessages.tripleCuts,
    parameter: wallPanelTripleCutsSelector,
    conflicts: wallPanelTripleCutConflictsSelector,
  },
};

const kitchenIslandOperations = {
  singleCut: {
    actionType: constants.CHANGE_KITCHEN_ISLAND_SINGLE_CUTS,
    message: parameterMessages.singleCuts,
    parameter: kitchenIslandSingleCutsSelector,
    conflicts: kitchenIslandSingleCutsConflictsSelector,
  },
  roundedCorner: {
    actionType: constants.CHANGE_KITCHEN_ISLAND_ROUNDED_CORNERS,
    message: parameterMessages.roundedCorners,
    parameter: kitchenIslandRoundedCornersSelector,
    conflicts: kitchenIslandRoundedCornersConflictsSelector,
  },
  cutout: {
    actionType: constants.CHANGE_KITCHEN_ISLAND_SQUARE_CUTS,
    message: parameterMessages.cutout,
    parameter: kitchenIslandSquareCutsSelector,
    conflicts: kitchenIslandSquareCutsConflictsSelector,
  },
};

export const planOperationParameters = _.map(planOperations, (v, operation) => ({ ...v, operation, category: conflictCategories.plan }));
export const worktopOperationParameters = _.map(worktopOperations, (v, operation) => ({ ...v, operation, category: conflictCategories.cutouts }));
export const wallPanelOperationParameters = _.map(wallPanelOperations, (v, operation) => ({ ...v, operation, category: conflictCategories.cutouts }));
export const kitchenIslandOperationParameters = _.map(kitchenIslandOperations, (v, operation) => ({ ...v, operation, category: conflictCategories.kitchenIsland }));

export const allOperationParameters = [
  ...planOperationParameters,
  ...kitchenIslandOperationParameters,
  ...wallPanelOperationParameters,
  ...worktopOperationParameters,
];
